// AuthAgentChat.js
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getCurrentAccount, getAccessToken } from "../azure-auth";
import { DirectLine } from "botframework-directlinejs";
import ReactWebChat, { createStore } from "botframework-webchat";
import Footer from "./Footer";
import Header from "./Header";
import "../uiux.css";
import { BASE_URL, URL } from "../urls";

const AuthAgentChat = ({ agentKey, agentName }) => {
  const agentIntroTextByKey = {
    askit: "Welcome to your IT support center and get instant assistance with all your IT-related needs.",
    askfinance: "Welcome to your finance support center and get instant assistance with all your finance-related needs.",
    askfinance_copilot: "Welcome to your finance support center and get instant assistance with all your finance-related needs.",
    askadmin: "Welcome to your admin support center and get instant assistance with all your admin-related needs.",
    askhr: "Welcome to your HR support center and get instant assistance with all your HR-related needs.",
  };
  const agentIntroText =
    agentIntroTextByKey[agentKey?.toLowerCase()] ||
    `Welcome to your ${agentName} support center and get instant assistance with your related needs.`;

  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const [chatId, setChatId] = useState("");
  const [directLineConfig, setDirectLineConfig] = useState(null);
  const [directLineError, setDirectLineError] = useState("");
  const [askHrTargetUrl, setAskHrTargetUrl] = useState("");
  const [workmateTargetUrl, setWorkmateTargetUrl] = useState("");

  // Check Authentication
  useEffect(() => {
    const account = getCurrentAccount();
    if (!account) {
      navigate("/");
    } else {
      setUser(account);
    }
    setLoading(false);
  }, [navigate]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previousPaddingBottom = document.body.style.paddingBottom;

    document.body.style.overflow = "hidden";
    document.body.style.paddingBottom = "0px";

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingBottom = previousPaddingBottom;
    };
  }, []);

  useEffect(() => {
    if (agentKey !== "askhr") return;

    const fetchAskHrUrl = async () => {
      try {
        const token = await getAccessToken();
        const userEmail = user ? (user.userName || user.username || user.email) : "";
        const userOID = user ? (user.localAccountId || user.oid) : "";

        const headers = { "Content-Type": "application/json" };
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        } else {
          // Provide mock headers for local/dev mode so backend can accept requests
          headers["X-User-Email"] = userEmail || "test@example.com";
          headers["X-User-Id"] = userOID || "test-oid";
        }

        const response = await fetch(URL.GET_TOKEN_ASKHR, { headers });
        const data = await response.json();
        if (data?.url) {
          setAskHrTargetUrl(data.url);
        }
      } catch (error) {
        console.error("Failed to fetch Ask HR URL:", error);
      }
    };

    fetchAskHrUrl();
  }, [agentKey, user]);

  useEffect(() => {
    if (agentKey !== "workmate") return;

    const fetchWorkmateUrl = async () => {
      try {
        const token = await getAccessToken();
        const userEmail = user ? (user.userName || user.username || user.email) : "";
        const userOID = user ? (user.localAccountId || user.oid) : "";

        const headers = { "Content-Type": "application/json" };
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        } else {
          headers["X-User-Email"] = userEmail || "test@example.com";
          headers["X-User-Id"] = userOID || "test-oid";
        }

        const response = await fetch(URL.GET_TOKEN_WORKMATE, { headers });
        const data = await response.json();
        if (data?.url) setWorkmateTargetUrl(data.url);
      } catch (error) {
        console.error("Failed to fetch Workmate URL:", error);
      }
    };

    fetchWorkmateUrl();
  }, [agentKey, user]);

  useEffect(() => {
    if (agentKey !== "askfinance_copilot") return;

    const fetchDirectLineToken = async () => {
      try {
        const token = await getAccessToken();
        const userEmail = user ? (user.userName || user.username || user.email) : "";
        const userOID = user ? (user.localAccountId || user.oid) : "";

        const headers = { "Content-Type": "application/json" };
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        } else {
          headers["X-User-Email"] = userEmail || "test@example.com";
          headers["X-User-Id"] = userOID || "test-oid";
        }

        const response = await fetch(URL.GET_TOKEN_ASKFINANCE_COPILOT, { headers });
        const data = await response.json();

        if (!data.token) {
          setDirectLineError("No token received.");
          return;
        }

        const directLine = new DirectLine({ token: data.token });
        const store = createStore({});

        setDirectLineConfig({ directLine, store });
      } catch (error) {
        console.error("Failed to fetch Direct Line token:", error);
        setDirectLineError("Failed to connect to Direct Line.");
      }
    };

    fetchDirectLineToken();
  }, [agentKey, agentName, user]);

  // ------------------------- IFRAME MODE (Ask HR) -------------------------
  if (agentKey === 'askhr') {
    const targetUrl = askHrTargetUrl || URL.ASKHR_LINE_TOKEN_URL;

    return (
      <div className="chatbot-container chat-fit-screen" style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', position: 'relative' }}>
        <img
          src="/aisleaiagents.png"
          alt="aisleaiagents Logo"
          className="logo"
          onClick={() => navigate("/dashboard")}
          style={{ cursor: "pointer", position: "absolute", top: "10px", left: "10px", zIndex: 1000 }}
        />
        
        <h1 className="chatbot-title" style={{ marginTop: '20px' }}>{agentName}</h1>
        <p
          style={{
            marginTop: '6px',
            marginBottom: '8px',
            color: 'rgba(255,255,255,0.85)',
            textAlign: 'center',
            fontSize: '16px',
            maxWidth: '780px',
            width: '92%',
          }}
        >
          {agentIntroText}
        </p>

        <div className="iframe-container" style={{
          flex: 1, width: "90%", maxWidth: "1000px", maxHeight: "calc(100vh - 300px)", margin: "20px auto 10px auto",
            borderRadius: "15px", overflow: "hidden", boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
            backgroundColor: "white", position: "relative", border: "1px solid #ddd"
        }}>
          <style>
            {`
              .responsive-iframe {
                width: 100%;
                height: 100%;
                border: none;
                margin-top: 0px;
                margin-bottom: 0px;
              }
            `}
          </style>

          <iframe 
            className="responsive-iframe"
            src={targetUrl} 
            title="Ask HR Agent" 
            allow="microphone; camera; clipboard-write; active-platform-authenticator" 
          />
        </div>

        <button
          className="logout-button"
          onClick={() => navigate("/dashboard")}
          style={{
            alignSelf: 'center',
            marginTop: '6px',
            marginBottom: '74px',
            zIndex: 1100,
          }}
        >
          Back
        </button>
        <Footer />
      </div>
    );
  }

  if (agentKey === 'workmate') {
    const targetUrl = workmateTargetUrl || "";

    return (
      <div className="chatbot-container chat-fit-screen" style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', position: 'relative' }}>
        <img
          src="/aisleaiagents.png"
          alt="aisleaiagents Logo"
          className="logo"
          onClick={() => navigate("/dashboard")}
          style={{ cursor: "pointer", position: "absolute", top: "10px", left: "10px", zIndex: 1000 }}
        />

        <h1 className="chatbot-title" style={{ marginTop: '20px' }}>{agentName}</h1>
        <p
          style={{
            marginTop: '6px',
            marginBottom: '8px',
            color: 'rgba(255,255,255,0.85)',
            textAlign: 'center',
            fontSize: '16px',
            maxWidth: '780px',
            width: '92%',
          }}
        >
          {agentIntroText}
        </p>

        <div className="iframe-container" style={{
          flex: 1, width: "90%", maxWidth: "1000px", maxHeight: "calc(100vh - 300px)", margin: "20px auto 10px auto",
            borderRadius: "15px", overflow: "hidden", boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
            backgroundColor: "white", position: "relative", border: "1px solid #ddd"
        }}>
          <style>
            {`
              .responsive-iframe {
                width: 100%;
                height: 100%;
                border: none;
                margin-top: 0px;
                margin-bottom: 0px;
              }
            `}
          </style>

          {targetUrl ? (
            <iframe 
              className="responsive-iframe"
              src={targetUrl} 
              title="Workmate Agent" 
              allow="microphone; camera; clipboard-write; active-platform-authenticator" 
            />
          ) : (
            <div style={{ padding: 24 }}>
              Loading Workmate...
            </div>
          )}
        </div>

        <button
          className="logout-button"
          onClick={() => navigate("/dashboard")}
          style={{
            alignSelf: 'center',
            marginTop: '6px',
            marginBottom: '74px',
            zIndex: 1100,
          }}
        >
          Back
        </button>
        <Footer />
      </div>
    );
  }

  if (agentKey === "askfinance_copilot") {
    if (loading) return <div>Loading...</div>;
    if (!user) return null;

    return (
      <div
        className="chatbot-container chat-fit-screen"
        style={{
          height: '100vh',
          minHeight: '100vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Header />
        <h1 className="chatbot-title">{agentName}</h1>
        <p
          style={{
            marginTop: '-6px',
            marginBottom: '14px',
            color: 'rgba(255,255,255,0.85)',
            textAlign: 'center',
            fontSize: '16px',
            maxWidth: '780px',
            width: '92%',
          }}
        >
          {agentIntroText}
        </p>

        <div
          className="text-space"
          style={{
            width: '92%',
            maxWidth: '1100px',
            flex: 1,
            maxHeight: 'calc(100vh - 320px)',
            minHeight: '0',
            borderRadius: '16px',
            overflow: "hidden",
            margin: '0 auto 12px auto',
            background: 'rgba(0, 0, 0, 0.35)',
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 12px rgba(0,173,255,0.35), 0 0 24px rgba(255,0,255,0.25)',
            display: "flex",
            flexDirection: "column",
            position: "relative",
          }}
        >
          {directLineConfig ? (
            <ReactWebChat
              directLine={directLineConfig.directLine}
              store={directLineConfig.store}
              style={{ height: "100%" }}
              styleOptions={{
                backgroundColor: 'rgba(0, 0, 0, 0.65)',
                bubbleBackground: 'rgba(255, 255, 255, 0.08)',
                bubbleBorderColor: 'rgba(255, 255, 255, 0.12)',
                bubbleBorderRadius: 18,
                bubbleTextColor: '#ffffff',
                bubbleFromUserBackground: '#6c5ce7',
                bubbleFromUserBorderRadius: 18,
                bubbleFromUserTextColor: '#ffffff',
                markdownRespectCRLF: true,
                rootHeight: '100%',
                rootWidth: '100%',
                sendBoxBackground: 'rgba(0,0,0,0.35)',
                sendBoxBorderTop: '1px solid rgba(255,255,255,0.12)',
                sendBoxButtonColor: '#a29bfe',
                sendBoxButtonColorOnDisabled: 'rgba(255,255,255,0.35)',
                sendBoxTextColor: '#ffffff',
                subtle: '#b8c2d1',
                timestampColor: 'rgba(255,255,255,0.5)',
              }}
            />
          ) : (
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: '15px',
                padding: '16px',
              }}
            >
              {directLineError || "Loading Chatbot..."}
            </div>
          )}
        </div>

        <button
          className="logout-button"
          onClick={() => navigate("/dashboard")}
          style={{
            alignSelf: 'center',
            marginTop: '6px',
            marginBottom: '74px',
            zIndex: 1100,
          }}
        >
          Back
        </button>
        <Footer />
      </div>
    );
  }

  
  
  const handleSendMessage = async () => {
    if (!input.trim()) return;
  
    const userMsg = { id: Date.now(), text: input, user: 'user' };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);
  
    try {
      const token = await getAccessToken();
      const userEmail = user ? (user.userName || user.username || user.email) : "";
      const userOID = user ? (user.localAccountId || user.oid) : "";
  
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      } else {
        headers['X-User-Email'] = userEmail || 'test@example.com';
        headers['X-User-Id'] = userOID || 'test-oid';
      }
  
       // Always POST to backend; backend decides GET/POST if needed
       const response = await fetch(`${BASE_URL}/api/v1/${agentKey}/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ message: userMsg.text, chat_id: chatId }),
      });
const data = await response.json();

if (data.chat_id) setChatId(data.chat_id);

const botText =
  data.response ||
  data.answer ||
  data.reply ||
  data.message ||
  "I couldn't understand that.";

setMessages(prev => [
  ...prev,
  {
    id: Date.now() + 2,
    text: botText,
    user: 'bot'
  }
]);

  
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages(prev => [...prev, { id: Date.now() + 1, text: "Error connecting to the agent.", user: 'bot', isError: true }]);
    } finally {
      setIsTyping(false);
    }
  };
  

  if (loading) return <div>Loading...</div>;
  if (!user) return null;

  return (
    <div
      className="chatbot-container chat-fit-screen"
      style={{
        height: '100vh',
        minHeight: '100vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Header />
      <h1 className="chatbot-title">{agentName}</h1>
      <p
        style={{
          marginTop: '-6px',
          marginBottom: '14px',
          color: 'rgba(255,255,255,0.85)',
          textAlign: 'center',
          fontSize: '16px',
          maxWidth: '780px',
          width: '92%',
        }}
      >
        {agentIntroText}
      </p>

      {/* ── Chat panel styled to match dashboard glass theme ── */}
      <div style={{
        width: '92%',
        maxWidth: '1100px',
        flex: 1,
        maxHeight: 'calc(100vh - 320px)',
        minHeight: '0',
        borderRadius: '16px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        margin: '0 auto 12px auto',
        background: 'rgba(0, 0, 0, 0.35)',
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 12px rgba(0,173,255,0.35), 0 0 24px rgba(255,0,255,0.25)',
      }}>

        {/* ── Messages area ── */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255,255,255,0.15) transparent',
        }}>
          {messages.map((msg) => (
            <div key={msg.id} style={{
              alignSelf: msg.user === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '78%',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
            }}>
              {/* Sender label */}
              {msg.user === 'bot' && (
                <span style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'rgba(0,210,255,0.85)',
                  paddingLeft: '14px',
                  letterSpacing: '0.4px',
                }}>
                  🤖 {agentName}
                </span>
              )}
              {msg.user === 'user' && (
                <span style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'rgba(180,150,255,0.9)',
                  paddingRight: '14px',
                  textAlign: 'right',
                  letterSpacing: '0.4px',
                }}>
                  You
                </span>
              )}
              {/* Bubble */}
              <div style={{
                padding: '12px 16px',
                borderRadius: msg.user === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                background: msg.user === 'user'
                  ? 'linear-gradient(135deg, #6c5ce7, #a29bfe)'
                  : 'rgba(255, 255, 255, 0.08)',
                border: msg.user === 'user'
                  ? 'none'
                  : '1px solid rgba(255, 255, 255, 0.12)',
                color: '#fff',
                fontSize: '15px',
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: '"Poppins", sans-serif',
                boxShadow: msg.user === 'user'
                  ? '0 4px 14px rgba(108,92,231,0.45)'
                  : '0 2px 8px rgba(0,0,0,0.3)',
              }}>
                {msg.text}
              </div>
            </div>
          ))}

          {/* Typing indicator */}
          {isTyping && (
            <div style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px' }}>
              {[0, 1, 2].map((i) => (
                <span key={i} style={{
                  width: '8px', height: '8px', borderRadius: '50%',
                  background: 'rgba(0,210,255,0.7)',
                  display: 'inline-block',
                  animation: `typingBounce 1.2s ease-in-out ${i * 0.2}s infinite`,
                }} />
              ))}
              <style>{`
                @keyframes typingBounce {
                  0%, 80%, 100% { transform: translateY(0); opacity: 0.5; }
                  40% { transform: translateY(-6px); opacity: 1; }
                }
              `}</style>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* ── Input area ── */}
        <div style={{
          padding: '14px 20px',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          background: 'rgba(0,0,0,0.25)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            placeholder={`Message ${agentName}…`}
            disabled={isTyping}
            style={{
              flex: 1,
              background: 'rgba(255,255,255,0.07)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '25px',
              padding: '10px 18px',
              color: '#fff',
              fontSize: '15px',
              outline: 'none',
              fontFamily: '"Poppins", sans-serif',
              transition: 'border-color 0.2s',
            }}
            onFocus={(e) => e.target.style.borderColor = 'rgba(0,173,255,0.6)'}
            onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.15)'}
          />
          <button
            onClick={handleSendMessage}
            disabled={isTyping || !input.trim()}
            style={{
              background: input.trim() && !isTyping
                ? 'linear-gradient(135deg, #6c5ce7, #a29bfe)'
                : 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: '50%',
              width: '42px',
              height: '42px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: input.trim() && !isTyping ? 'pointer' : 'not-allowed',
              boxShadow: input.trim() && !isTyping ? '0 4px 14px rgba(108,92,231,0.5)' : 'none',
              transition: 'all 0.2s ease',
              flexShrink: 0,
            }}
          >
            <svg style={{ width: '18px', height: '18px', fill: 'none', stroke: '#fff', strokeWidth: '2', strokeLinecap: 'round', strokeLinejoin: 'round' }} viewBox="0 0 24 24">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
      </div>

      <button
        className="logout-button"
        onClick={() => navigate("/dashboard")}
        style={{
          alignSelf: 'center',
          marginTop: '6px',
          marginBottom: '74px',
          zIndex: 1100,
        }}
      >
        Back
      </button>
      <Footer />
    </div>
  );
};

export default AuthAgentChat;